/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useRef, useState, useMemo, useEffect } from 'react';
import { camelCase, isEmpty } from 'lodash-es';
import { useLocation, useNavigate } from 'react-router-dom';
import { ignoredQueryParamsForLoggedInUser } from '../pages';
import { useServerErrorVar } from '../utils/apollo-vars';
import {
  getSavedURLParams,
  removeSavedURLParams,
} from '../utils/saved-url-params';
import {
  useAlertModal,
  useURLParams,
  type AlertModalContentType,
} from '../hooks';

const useURLPathNotifications = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const serverError = useServerErrorVar();
  const { dispatch: alertModalDispatch } = useAlertModal();
  const initialLoadRef = useRef(true);
  const [content, setContent] = useState<AlertModalContentType | null>(null);
  const [data, setData] = useState<unknown>(undefined);

  const { urlSearchParamsAsArray } = useURLParams();

  const urlParams = useMemo(() => {
    const savedURLParams = getSavedURLParams();
    const source = !isEmpty(urlSearchParamsAsArray)
      ? urlSearchParamsAsArray
      : !isEmpty(savedURLParams)
        ? savedURLParams
        : [];
    return source.map(([key, value]) => [camelCase(key), value]);
  }, [urlSearchParamsAsArray]);

  useEffect(() => {
    if (serverError || isEmpty(urlParams)) return;

    for (const [key, value] of urlParams) {
      const content = notificationsData[key]?.[value];
      if (!content) continue;
      const data = urlParams
        .filter(([arrKey]) => arrKey !== key)
        .reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: value,
          }),
          {}
        );
      setContent(content);
      setData(!isEmpty(data) ? data : undefined);
      break;
    }
  }, [serverError, urlParams]);

  // Open modal if there is content and it's initial load
  useEffect(() => {
    if (initialLoadRef.current && !!content) {
      initialLoadRef.current = false;
      alertModalDispatch(['setModalContent', content, data]);
      navigate(
        {
          pathname,
          search: '',
        },
        { replace: true }
      );
      removeSavedURLParams();
    }
  }, [alertModalDispatch, pathname, content, data, navigate]);

  /*
   * Clear search string for started notifications
   * */
  useEffect(() => {
    if (ignoredQueryParamsForLoggedInUser.some((option) => option === search)) {
      navigate(
        {
          pathname,
          search: '',
        },
        { replace: true }
      );
    }
  }, [search, pathname, navigate]);

  return null;
};

/*
 * Started is not handled.
 * Created for unauthorised users
 * Notification itself for logged in user
 * comes from useUserNotifications */
// ?exec_rebalance=started
// ?deposit_crypto=started
// ?sell_crypto=started
// ===
// ?exec_rebalance=error
// ?exec_rebalance=expired
// ?exec_rebalance=reused
// ?sell_crypto=error
// ?sell_crypto=expired
// ?sell_crypto=reused
// ?sell_crypto=error-left-money
// ?deposit_crypto=error
// ?deposit_crypto=expired
// ?deposit_crypto=reused
// ?deposit_crypto=error-left-money
// ?payment=subscription
// ?payment=single&invoice_id=8
// ?email_change=success
// ?email_change=error
// ?email_change=expired
// ?remove-user=expired

// Going to be added for login screen
// sellCrypto=started
// depositCrypto=started

const notificationsData: Record<
  string,
  Partial<Record<string, AlertModalContentType>>
> = {
  execRebalance: {
    error: 'rebalanceOrSellError',
    expired: 'rebalanceOrSellExpired',
    reused: 'rebalanceOrBuyOrSellReused',
  },
  sellCrypto: {
    error: 'rebalanceOrSellError',
    expired: 'rebalanceOrSellExpired',
    reused: 'rebalanceOrBuyOrSellReused',
    'error-left-money': 'sellLeftMoneyError',
  },
  depositCrypto: {
    error: 'depositError',
    expired: 'depositExpired',
    reused: 'rebalanceOrBuyOrSellReused',
    'error-left-money': 'depositLeftMoneyError',
  },
  payment: {
    subscription: 'paymentSubscription',
    single: 'paymentSingle',
  },
  emailChange: {
    success: 'changeEmailSuccess',
    error: 'changeEmailError',
    expired: 'changeEmailExpired',
  },
  removeUser: {
    expired: 'removeAccountLinkExpired',
  },
};

export { useURLPathNotifications };
